import {
  type AccountInput,
  type AffordabilityRequest,
  accountReadSchema,
  activityItemReadSchema,
  activityPageReadSchema,
  affordabilityReadSchema,
  apiEnvelopeSchema,
  apiErrorSchema,
  briefingReadSchema,
  type CsvConfirmRequest,
  type CsvPreviewRequest,
  coverageReadSchema,
  csvImportReadSchema,
  csvPreviewReadSchema,
  type DebtInput,
  debtReadSchema,
  exportReadSchema,
  type GoalInput,
  goalReadSchema,
  type IncomeSourceInput,
  incomeEventReadSchema,
  type LifeDayInput,
  type ManualTransactionInput,
  needsConfirmationReadSchema,
  type ObligationInput,
  obligationReadSchema,
  planReadSchema,
  profileReadSchema,
  purposeReadSchema,
  type RecommendationOutcomeInput,
  type SparkEntryInput,
  settingsReadSchema,
  sparkReadSchema,
} from "@moneymap/contracts";
import { z } from "zod";
import { publicEnvironment } from "../env";
import type {
  AccountRead,
  ActivityItem,
  ActivityPageRead,
  ApiEnvelope,
  BriefingRead,
  ConnectionRead,
  CoverageRead,
  CsvImportRead,
  CsvPreviewRead,
  DebtRead,
  ExportRead,
  GoalRead,
  IncomeEventRead,
  NeedsConfirmationRead,
  ObligationRead,
  OnboardingProgressInput,
  PlanRead,
  ProfileRead,
  PurposeRead,
  SettingsRead,
  SparkRead,
} from "./types";

const connectionReadSchema = z
  .object({
    id: z.string().uuid(),
    provider: z.enum(["manual", "csv", "plaid"]),
    provider_environment: z.enum(["sandbox", "development", "production"]).nullable().optional(),
    status: z.enum([
      "active",
      "syncing",
      "stale",
      "reauth_required",
      "error",
      "disconnected",
      "disabled",
    ]),
    last_success_at: z.string().nullable().optional(),
    last_error_code: z.string().nullable().optional(),
    created_at: z.string(),
  })
  .transform((value) => ({
    id: value.id,
    provider: value.provider,
    ...(value.provider_environment ? { providerEnvironment: value.provider_environment } : {}),
    status: value.status,
    ...(value.last_success_at ? { lastSuccessAt: value.last_success_at } : {}),
    ...(value.last_error_code ? { lastErrorCode: value.last_error_code } : {}),
    createdAt: value.created_at,
  })) as unknown as z.ZodType<ConnectionRead>;

export class ApiClientError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly isRetryable: boolean;
  readonly details?: Record<string, unknown>;

  constructor(input: {
    code: string;
    message: string;
    requestId?: string;
    isRetryable?: boolean;
    details?: Record<string, unknown>;
  }) {
    super(input.message);
    this.name = "ApiClientError";
    this.code = input.code;
    this.isRetryable = input.isRetryable ?? false;
    if (input.requestId) this.requestId = input.requestId;
    if (input.details) this.details = input.details;
  }
}

type RequestOptions<T> = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  schema?: z.ZodType<T>;
  idempotent?: boolean;
  version?: number;
};

function idempotencyKey(): string {
  if (typeof crypto.randomUUID !== "function") {
    throw new Error("This browser cannot create secure request identifiers.");
  }
  return crypto.randomUUID();
}

async function readResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type");
  if (response.status === 204) return undefined;
  if (!contentType?.includes("application/json")) {
    const requestId = response.headers.get("x-request-id");
    throw new ApiClientError({
      code: "INVALID_RESPONSE",
      message: "IterNest Money received an unreadable response. Please try again.",
      ...(requestId ? { requestId } : {}),
      isRetryable: response.status >= 500,
    });
  }
  return response.json() as Promise<unknown>;
}

export class MoneyMapApiClient {
  constructor(private readonly accessToken: string) {}

  private async request<T>(path: string, options: RequestOptions<T> = {}): Promise<ApiEnvelope<T>> {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new ApiClientError({
        code: "OFFLINE",
        message: "IterNest Money is offline. Your last view is unchanged; reconnect before saving.",
        isRetryable: true,
      });
    }

    const headers = new Headers({
      Accept: "application/json",
      Authorization: "Bearer " + this.accessToken,
    });
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    if (options.idempotent) headers.set("Idempotency-Key", idempotencyKey());
    if (options.version !== undefined) headers.set("If-Match", String(options.version));

    let response: Response;
    try {
      response = await fetch(publicEnvironment.apiBaseUrl + "/api/v1" + path, {
        method: options.method ?? "GET",
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        cache: "no-store",
        credentials: "same-origin",
      });
    } catch {
      throw new ApiClientError({
        code: "NETWORK_ERROR",
        message:
          "IterNest Money could not reach the server. Your saved financial state was not changed.",
        isRetryable: true,
      });
    }

    const payload = await readResponseBody(response);
    if (!response.ok) {
      const parsedError = apiErrorSchema.safeParse(payload);
      if (parsedError.success) {
        throw new ApiClientError({
          code: parsedError.data.error.code,
          message: parsedError.data.error.message,
          requestId: parsedError.data.error.requestId,
          isRetryable: parsedError.data.error.retryable,
          ...(parsedError.data.error.details ? { details: parsedError.data.error.details } : {}),
        });
      }
      throw new ApiClientError({
        code: "HTTP_" + String(response.status),
        message:
          response.status >= 500
            ? "IterNest Money could not complete that request. Your previous state is still safe."
            : "IterNest Money could not complete that request.",
        ...(response.headers.get("x-request-id")
          ? { requestId: response.headers.get("x-request-id") as string }
          : {}),
        isRetryable: response.status >= 500,
      });
    }

    if (options.schema) {
      const parsed = apiEnvelopeSchema(options.schema).safeParse(payload);
      if (!parsed.success) {
        throw new ApiClientError({
          code: "INVALID_RESPONSE",
          message: "IterNest Money received an incomplete response. Please refresh and try again.",
        });
      }
      return parsed.data as ApiEnvelope<T>;
    }
    if (
      !payload ||
      typeof payload !== "object" ||
      !("data" in payload) ||
      !("requestId" in payload)
    ) {
      throw new ApiClientError({
        code: "INVALID_RESPONSE",
        message: "IterNest Money received an incomplete response. Please refresh and try again.",
      });
    }
    return payload as ApiEnvelope<T>;
  }

  getMe() {
    return this.request<ProfileRead>("/me", { schema: profileReadSchema });
  }

  updateMe(input: Partial<ProfileRead> & { household?: Partial<ProfileRead["household"]> }) {
    return this.request<ProfileRead>("/me", {
      method: "PATCH",
      body: input,
      idempotent: true,
      schema: profileReadSchema,
    });
  }

  saveOnboardingProgress(input: OnboardingProgressInput) {
    const body: Record<string, unknown> = { onboardingState: input.step };
    for (const key of ["displayName", "timezone", "locale"] as const) {
      const value = input.data[key];
      if (typeof value === "string") body[key] = value;
    }
    for (const key of ["householdName", "baseCurrency"] as const) {
      const value = input.data[key];
      if (typeof value === "string") body[key] = value;
    }
    return this.request<ProfileRead>("/me", {
      method: "PATCH",
      body,
      idempotent: true,
      schema: profileReadSchema,
    });
  }

  getBriefing() {
    return this.request<BriefingRead | null>("/briefing", {
      schema: briefingReadSchema.nullable(),
    });
  }

  acknowledgeBriefing(briefingId: string) {
    return this.request<{ acknowledgedAt: string }>("/briefing/" + briefingId + "/acknowledge", {
      method: "POST",
      body: { displayedAt: new Date().toISOString() },
      idempotent: true,
    });
  }

  getRecommendation(recommendationId: string) {
    return this.request<import("@moneymap/contracts").BriefingRead["todayAction"]>(
      "/recommendations/" + recommendationId,
    );
  }

  recordRecommendationOutcome(recommendationId: string, input: RecommendationOutcomeInput) {
    return this.request<{ status: string; recalculationStatus: string }>(
      "/recommendations/" + recommendationId + "/outcomes",
      { method: "POST", body: input, idempotent: true },
    );
  }

  getCoverage(horizon: string, customEndDate?: string) {
    const params = new URLSearchParams({ horizon });
    if (customEndDate) params.set("customEndDate", customEndDate);
    return this.request<CoverageRead>("/coverage?" + params.toString(), {
      schema: coverageReadSchema,
    });
  }

  getCoverageEvidence(runId: string) {
    return this.request<{ evidence: CoverageRead["evidence"] }>("/coverage/" + runId + "/evidence");
  }

  getAccounts() {
    return this.request<AccountRead[]>("/accounts", { schema: z.array(accountReadSchema) });
  }

  getConnections() {
    return this.request<ConnectionRead[]>("/connections", {
      schema: z.array(connectionReadSchema),
    });
  }

  createPlaidLinkSession() {
    return this.request<{
      linkToken: string;
      expiration: string;
      environment: "sandbox" | "development" | "production";
    }>("/connections/link-session", {
      method: "POST",
      body: { consent: true },
      schema: z.object({
        linkToken: z.string().min(1),
        expiration: z.string(),
        environment: z.enum(["sandbox", "development", "production"]),
      }),
    });
  }

  exchangePlaidPublicToken(publicToken: string) {
    return this.request<{ id: string; status: string }>("/connections/exchange", {
      method: "POST",
      body: { publicToken },
      idempotent: true,
      schema: z.object({ id: z.string().uuid(), status: z.string() }).passthrough(),
    });
  }

  createPlaidUpdateSession(connectionId: string) {
    return this.request<{
      linkToken: string;
      expiration: string;
      environment: "sandbox" | "development" | "production";
      updateMode: boolean;
    }>(`/connections/${connectionId}/update-session`, {
      method: "POST",
      body: {},
      schema: z.object({
        linkToken: z.string().min(1),
        expiration: z.string(),
        environment: z.enum(["sandbox", "development", "production"]),
        updateMode: z.boolean(),
      }),
    });
  }

  disconnectPlaid(connectionId: string) {
    return this.request<{ connectionId: string; status: string }>(`/connections/${connectionId}`, {
      method: "DELETE",
      body: { confirmation: "DISCONNECT" },
    });
  }

  createAccount(input: AccountInput) {
    return this.request<AccountRead>("/accounts", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: accountReadSchema,
    });
  }

  updateAccount(accountId: string, input: Partial<AccountInput>, version: number) {
    return this.request<AccountRead>("/accounts/" + accountId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: accountReadSchema,
    });
  }

  deleteAccount(accountId: string, version: number) {
    return this.request<{ recalculationStatus: string }>("/accounts/" + accountId, {
      method: "DELETE",
      body: { confirmation: "DELETE" },
      idempotent: true,
      version,
    });
  }

  getPurposes() {
    return this.request<PurposeRead[]>("/purposes", { schema: z.array(purposeReadSchema) });
  }

  createPurpose(
    input: Pick<
      PurposeRead,
      "name" | "type" | "priority" | "protectionLevel" | "protectedFloorMinor"
    >,
  ) {
    return this.request<PurposeRead>("/purposes", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: purposeReadSchema,
    });
  }

  updatePurpose(purposeId: string, input: Partial<PurposeRead>, version: number) {
    return this.request<PurposeRead>("/purposes/" + purposeId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: purposeReadSchema,
    });
  }

  getActivity(filters: Record<string, string | undefined>) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
    const suffix = params.size > 0 ? "?" + params.toString() : "";
    return this.request<ActivityPageRead>("/activity" + suffix, {
      schema: activityPageReadSchema,
    });
  }

  getActivityItem(itemId: string) {
    return this.request<ActivityItem>("/activity/" + itemId, { schema: activityItemReadSchema });
  }

  createManualTransaction(input: ManualTransactionInput) {
    return this.request<ActivityItem>("/activity", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: activityItemReadSchema,
    });
  }

  updateManualTransaction(
    transactionId: string,
    input: Partial<ManualTransactionInput>,
    version: number,
  ) {
    return this.request<ActivityItem>("/activity/" + transactionId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: activityItemReadSchema,
    });
  }

  deleteManualTransaction(transactionId: string, version: number) {
    return this.request<{ recalculationStatus: string }>("/activity/" + transactionId, {
      method: "DELETE",
      body: { reason: "Deleted by user" },
      idempotent: true,
      version,
    });
  }

  correctActivity(transactionId: string, input: Record<string, unknown>) {
    return this.request<ActivityItem>("/activity/" + transactionId + "/corrections", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: activityItemReadSchema,
    });
  }

  previewCsv(input: CsvPreviewRequest) {
    return this.request<CsvPreviewRead>("/imports/csv/preview", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: csvPreviewReadSchema,
    });
  }

  confirmCsv(input: CsvConfirmRequest) {
    return this.request<CsvImportRead>("/imports/csv/confirm", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: csvImportReadSchema,
    });
  }

  getImportHistory() {
    return this.request<CsvImportRead[]>("/imports/csv", {
      schema: z.array(csvImportReadSchema),
    });
  }

  getPlan(from?: string, to?: string) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const suffix = params.size > 0 ? "?" + params.toString() : "";
    return this.request<PlanRead>("/plan" + suffix, { schema: planReadSchema });
  }

  getObligation(obligationId: string) {
    return this.request<ObligationRead>("/obligations/" + obligationId, {
      schema: obligationReadSchema,
    });
  }

  createObligation(input: ObligationInput) {
    return this.request<ObligationRead>("/obligations", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: obligationReadSchema,
    });
  }

  updateObligation(obligationId: string, input: Partial<ObligationInput>, version: number) {
    return this.request<ObligationRead>("/obligations/" + obligationId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: obligationReadSchema,
    });
  }

  saveFundingPlan(occurrenceId: string, input: Record<string, unknown>) {
    return this.request<ObligationRead>(
      "/obligation-occurrences/" + occurrenceId + "/funding-plan",
      {
        method: "POST",
        body: { contributions: [input] },
        idempotent: true,
        schema: obligationReadSchema,
      },
    );
  }

  createIncomeSource(input: IncomeSourceInput) {
    return this.request<{ id: string }>("/income-sources", {
      method: "POST",
      body: input,
      idempotent: true,
    });
  }

  getSpark() {
    return this.request<SparkRead>("/spark", { schema: sparkReadSchema });
  }

  createSparkEntry(input: SparkEntryInput) {
    return this.request<SparkRead>("/spark/entries", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: sparkReadSchema,
    });
  }

  createLifeDay(input: LifeDayInput) {
    return this.request<SparkRead>("/life-days", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: sparkReadSchema,
    });
  }

  getGoals() {
    return this.request<GoalRead[]>("/goals", { schema: z.array(goalReadSchema) });
  }

  createGoal(input: GoalInput) {
    return this.request<GoalRead>("/goals", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: goalReadSchema,
    });
  }

  updateGoal(goalId: string, input: Partial<GoalInput>, version: number) {
    return this.request<GoalRead>("/goals/" + goalId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: goalReadSchema,
    });
  }

  getDebts() {
    return this.request<DebtRead[]>("/debts", { schema: z.array(debtReadSchema) });
  }

  createDebt(input: DebtInput) {
    return this.request<DebtRead>("/debts", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: debtReadSchema,
    });
  }

  updateDebt(debtId: string, input: Partial<DebtInput>, version: number) {
    return this.request<DebtRead>("/debts/" + debtId, {
      method: "PATCH",
      body: input,
      idempotent: true,
      version,
      schema: debtReadSchema,
    });
  }

  getIncomeEvents() {
    return this.request<IncomeEventRead[]>("/income-events", {
      schema: z.array(incomeEventReadSchema),
    });
  }

  createIncomeEvent(input: Record<string, unknown>) {
    return this.request<IncomeEventRead>("/income-events", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: incomeEventReadSchema,
    });
  }

  evaluateAffordability(input: AffordabilityRequest) {
    return this.request("/affordability/evaluate", {
      method: "POST",
      body: input,
      idempotent: true,
      schema: affordabilityReadSchema,
    });
  }

  getNeedsConfirmation() {
    return this.request<NeedsConfirmationRead>("/needs-confirmation", {
      schema: needsConfirmationReadSchema,
    });
  }

  resolveConfirmation(itemId: string, input: Record<string, unknown>) {
    return this.request<{ recalculationStatus: string }>("/needs-confirmation/" + itemId, {
      method: "POST",
      body: {
        action: input.action ?? input.decision,
        ...(typeof input.note === "string" ? { note: input.note } : {}),
      },
      idempotent: true,
    });
  }

  getSettings() {
    return this.request<SettingsRead>("/settings", { schema: settingsReadSchema });
  }

  updateSettings(input: Partial<SettingsRead>, version: number) {
    const household = input.household;
    const body = {
      ...input,
      ...(household
        ? {
            timezone: input.profile?.timezone,
            baseCurrency: household.baseCurrency,
            financialHorizonDays: household.financialHorizonDays,
            minimumActionMinor: household.minimumActionMinor,
            householdBufferMinor: household.householdBufferMinor,
            debtStrategy: household.debtStrategy,
          }
        : {}),
    };
    return this.request<SettingsRead>("/settings", {
      method: "PATCH",
      body,
      idempotent: true,
      version,
      schema: settingsReadSchema,
    });
  }

  requestExport() {
    return this.request<ExportRead>("/data-export", {
      method: "POST",
      body: { format: "json" },
      idempotent: true,
      schema: exportReadSchema,
    });
  }

  getExport(exportId: string) {
    return this.request<ExportRead>("/data-export/" + exportId, { schema: exportReadSchema });
  }

  requestAccountDeletion(input: { confirmation: string }) {
    return this.request<{ deletionRequestId: string; status: string }>("/account-deletion", {
      method: "POST",
      body: input,
      idempotent: true,
    });
  }
}

export function shouldRetryRequest(failureCount: number, error: Error): boolean {
  return failureCount < 2 && error instanceof ApiClientError && error.isRetryable;
}
